/**
 * Portal invoice detail (tracker item 45): lines, discounts, taxes, payments
 * applied and the balance, with Pay now (PortalPayPanel). Stacked cards on a
 * phone; nothing wider than the screen.
 */
import React, { useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { usePortal, usePortalApi } from './PortalContext';
import { usePortalLoad } from './usePortalLoad';
import { FailureBlock, LoadingBlock } from './PortalStates';
import { PortalPayPanel } from './PortalPayPanel';
import { invoiceDate, invoiceMoney } from '../invoiceUi';
import { cardLabel } from '../../features/online-payments/onlinePaymentsUi';
import { localToday, portalAddress, portalInvoiceStatusClass, portalInvoiceStatusLabel } from '../../utils/portal';

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between gap-6 ${strong ? 'border-t-2 border-gray-800 pt-2 text-base font-bold' : ''}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

export function PortalInvoicePage() {
  const { id = '' } = useParams<{ id: string }>();
  const { me } = usePortal();
  const [search] = useSearchParams();
  const api = usePortalApi();
  const load = useCallback(() => api.getPortalInvoice(id), [api, id]);
  const { data: invoice, loading, failure, reload } = usePortalLoad(load, 'We could not load this invoice.', id);

  if (loading && !invoice) return <LoadingBlock label="Loading invoice" />;
  if (failure) {
    return (
      <div className="space-y-3">
        <FailureBlock failure={failure} onRetry={failure.kind === 'not-found' ? undefined : () => void reload()} />
        <Link to="/portal/invoices" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">Back to invoices</Link>
      </div>
    );
  }
  if (!invoice) return null;

  const label = portalInvoiceStatusLabel(invoice, localToday());
  const hasDiscounts = Number(invoice.totalDiscount) > 0;
  return (
    <div className="space-y-4">
      <Link to="/portal/invoices" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-blue-700 hover:underline">&larr; Invoices</Link>
      <section className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-lg font-bold text-gray-900">Invoice {invoice.invoiceNumber}</h1>
          <span className={`rounded px-2 py-0.5 text-xs font-semibold ${portalInvoiceStatusClass(label)}`}>{label}</span>
        </div>
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-gray-600">Invoice date</dt><dd>{invoiceDate(invoice.invoiceDate)}</dd>
          <dt className="text-gray-600">Due date</dt><dd>{invoiceDate(invoice.dueDate)}</dd>
          {invoice.property && (
            <>
              <dt className="text-gray-600">Property</dt>
              <dd className="break-words" data-testid="invoice-property">
                {portalAddress(invoice.property)}
                {me.properties.find((p) => p.id === invoice.property?.id)?.relation === 'BILLED_TO_YOU' && <span className="block text-xs text-gray-500">Billed to you</span>}
              </dd>
            </>
          )}
          {invoice.periodStartDate && (
            <><dt className="text-gray-600">Service period</dt><dd>{invoiceDate(invoice.periodStartDate)} to {invoiceDate(invoice.periodEndDate)}</dd></>
          )}
        </dl>
      </section>

      <section aria-labelledby="lines-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <h2 id="lines-heading" className="mb-2 text-sm font-semibold text-gray-600">What you were charged for</h2>
        <ul className="divide-y divide-gray-100" aria-label="Invoice lines">
          {invoice.lines.map((line, index) => (
            <li key={index} className="py-3 text-sm" data-testid="invoice-line">
              <div className="flex justify-between gap-4">
                <span className="min-w-0 break-words font-medium text-gray-900">{line.description}</span>
                <span className="whitespace-nowrap font-semibold">{invoiceMoney(line.amount)}</span>
              </div>
              <div className="mt-0.5 text-xs text-gray-500">
                {line.quantity} × {invoiceMoney(line.unitPrice)}
                {Number(line.discount) > 0 && ` · discount −${invoiceMoney(line.discount)}`}
                {Number(line.tax) > 0 && ` · tax ${invoiceMoney(line.tax)}${Number(line.taxRate) > 0 ? ` (${line.taxRate}%)` : ''}`}
              </div>
            </li>
          ))}
        </ul>
        <dl className="mt-3 ml-auto w-full max-w-xs space-y-1 text-sm" aria-label="Invoice totals">
          <Row label="Subtotal" value={invoiceMoney(invoice.subtotal)} />
          {invoice.discounts.map((d, i) => <Row key={i} label={`Discount: ${d.name}`} value={`−${invoiceMoney(d.amount)}`} />)}
          {hasDiscounts && invoice.discounts.length === 0 && <Row label="Discounts" value={`−${invoiceMoney(invoice.totalDiscount)}`} />}
          <Row label="Tax" value={invoiceMoney(invoice.totalTax)} />
          <Row label="Total" value={invoiceMoney(invoice.total)} />
          <Row label="Paid" value={invoiceMoney(invoice.amountPaid)} />
          <Row label="Balance due" value={invoiceMoney(invoice.balanceDue)} strong />
        </dl>
      </section>

      {invoice.payments.length > 0 && (
        <section aria-labelledby="payments-heading" className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <h2 id="payments-heading" className="mb-2 text-sm font-semibold text-gray-600">Payments received</h2>
          <ul className="divide-y divide-gray-100" aria-label="Payments applied">
            {invoice.payments.map((p, i) => (
              <li key={i} className="flex justify-between gap-4 py-2 text-sm" data-testid="invoice-payment">
                <span className="min-w-0 break-words">
                  {invoiceDate(p.date)} · {p.kind === 'CREDIT' ? 'Credit applied from an earlier payment' : p.cardLast4 ? cardLabel({ brand: p.cardBrand, last4: p.cardLast4 }) : p.method.replace(/_/g, ' ').toLowerCase()}
                </span>
                <span className="whitespace-nowrap font-semibold">{invoiceMoney(p.amount)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <PortalPayPanel
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoiceNumber}
        balanceDue={invoice.balanceDue}
        openOnLoad={search.get('pay') === '1'}
        onPaid={() => void reload()}
      />
    </div>
  );
}
